import React, { useEffect, useRef, useState } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { useTopics } from './hooks/useTopics';
import { TopicCard } from './TopicCard';
import GenericCardLoading from 'src/components/BeaconV2Components/GenericCardLoading/GenericCardLoading';
import { SlPaginator, SlPaginatorApi } from 'src/components/BeaconV2Components/SlPaginator';
import { TopicCardRecord, TopicPillFilter, TopicType } from './types';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlClickThruAction } from 'src/components/BeaconV2Components/SlClickThruAction';
import TimePeriodDropdown, {
  getAtlasBeaconTimePeriods
} from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import ComparisonPeriodDropdown from 'src/components/BeaconV2Components/common/ComparisonPeriodDropdown';
import { SlPill } from 'src/components/BeaconV2Components/SlPill';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { DownChevronWhite, SlTripleFolderIcon } from 'src/components/SvgIcons';
import { SlMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { useAppSelector, useDebouncedValue, useHistory } from 'src/utils/Hooks';
import { DeleteTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/modals/DeleteTopicModal';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import {
  StorageSource,
  useTopicBuilderActions
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import SlPlaceholderMessage from 'src/components/BeaconV2Components/SlMessagePlaceholder/SlMessagePlaceholder';
import { NoDataIcon } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { useStacklineTheme } from '@stackline/ui';
import { DeleteTopicGroupModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/modals/DeleteTopicGroupModal';
import { AppFilterButton } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterButton';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';

const MAX_ITEMS_PER_PAGE = 20;

function TopicsGridNoMemo({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(304px, 1fr))',
        gap: '24px'
      }}
    >
      {children}
    </div>
  );
}

const TopicsGrid = React.memo(TopicsGridNoMemo);

function TopicsResultsNoMemo({
  selectedTopicType,
  selectedTopicPillFilter,
  debouncedInput
}: {
  selectedTopicType: TopicType;
  selectedTopicPillFilter: TopicPillFilter;
  debouncedInput: string;
}) {
  const paginatorRef = useRef<SlPaginatorApi>(null);
  const { openModal: setAddTopicGroupModalOpen } = useAddTopicGroupStoreActions();
  const history = useHistory();
  const { searchParams, filterParams } = useAppSelector((state) => state.app.queryParams);
  const { storage } = useTopicBuilderActions();
  const [page, setPage] = useState(1);
  const theme = useStacklineTheme();

  useEffect(() => {
    /* eslint-disable no-unused-expressions */
    paginatorRef.current?.setPage(1);
    setPage(1);
  }, [selectedTopicType, selectedTopicPillFilter, debouncedInput, searchParams, filterParams]);

  const isFilteredOnTopicGroups = selectedTopicPillFilter === TopicPillFilter.TopicGroups;

  const { topics, isLoading } = useTopics({
    topicFilter: selectedTopicPillFilter,
    topicType: selectedTopicType,
    searchInput: debouncedInput,
    page,
    itemsPerPage: MAX_ITEMS_PER_PAGE
  });

  const handleCreateTopicClick = () => {
    storage.save(null, StorageSource.CREATE);
    history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
  };

  return (
    <div
      style={{
        marginTop: '32px'
      }}
    >
      <SlPaginator
        ref={paginatorRef}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        items={topics.filter((topic) => {
          if (selectedTopicPillFilter === TopicPillFilter.Topics) {
            return topic.type === 'topic';
          }
          if (selectedTopicPillFilter === TopicPillFilter.TopicGroups) {
            return topic.type === 'topicGroup';
          }
          return true;
        })}
        renderItems={(items: TopicCardRecord[]) => {
          if (items.length < 1) {
            return (
              <Flex marginTop="64px" justifyContent="center">
                <Flex gap="sm" flexDirection="column">
                  <SlPlaceholderMessage
                    maxWidth={320}
                    renderIcon={() => {
                      return (
                        <NoDataIcon
                          icon={<SlTripleFolderIcon />}
                          iconBackgroundStyle={{
                            backgroundColor: theme.colors.secondaryGhost
                          }}
                        />
                      );
                    }}
                    title={isFilteredOnTopicGroups ? 'Add topic groups' : 'Add topics'}
                    subtitle={
                      isFilteredOnTopicGroups
                        ? 'There are currently no topic groups to display. You can create your own topic group to gather insights.'
                        : 'There are currently no topics to display.'
                    }
                  />

                  <SlClickThruAction
                    textSx={{ fontSize: '16px' }}
                    onClick={() => {
                      isFilteredOnTopicGroups ? setAddTopicGroupModalOpen() : handleCreateTopicClick();
                    }}
                    label={isFilteredOnTopicGroups ? 'Create topic group' : 'Create a topic'}
                  />
                </Flex>
              </Flex>
            );
          }
          return (
            <TopicsGrid>
              {items.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </TopicsGrid>
          );
        }}
        itemsPerPage={MAX_ITEMS_PER_PAGE}
        loading={isLoading}
        renderLoader={() => (
          <TopicsGrid>
            {Array.from({ length: 20 }).map((_, i) => (
              <GenericCardLoading key={i} width={304} height={484} />
            ))}
          </TopicsGrid>
        )}
      />
    </div>
  );
}

const TopicsResults = React.memo(TopicsResultsNoMemo);

/**
 * Topics with Topic Groups view
 */
export function TopicsViewV2() {
  const [selectedTopicPillFilter, setSelectedTopicPillFilter] = useState<TopicPillFilter>(TopicPillFilter.All);
  const retailerId = useAppSelector((state) => state.retailer.id);
  const isAmazonUS = `${retailerId}` === '1';

  // Initialize selectedTopicType based on retailer
  const [selectedTopicType, setSelectedTopicType] = useState<TopicType>(isAmazonUS ? TopicType.All : TopicType.Custom);

  const [addTopicMenuOpen, setAddTopicMenuOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedInput = useDebouncedValue(searchInput, 500);
  const paginatorRef = useRef<SlPaginatorApi>(null);
  const addTopicButtonRef = useRef(null);
  const { openModal: setAddTopicGroupModalOpen } = useAddTopicGroupStoreActions();
  const history = useHistory();
  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  const { storage } = useTopicBuilderActions();

  const handleTopicTypeChange = (option: { id: string }) => {
    const newTopicType = option.id as TopicType;
    setSelectedTopicType(newTopicType);
  };

  const filterOptions = isAmazonUS
    ? [
        {
          label: 'View All',
          id: TopicType.All
        },
        {
          label: 'View Standard',
          id: TopicType.Standard
        },
        {
          label: 'View Custom',
          id: TopicType.Custom
        },
        {
          label: 'View Owned',
          id: TopicType.Owned
        }
      ]
    : [
        {
          label: 'View Custom',
          id: TopicType.Custom
        },
        {
          label: 'View Owned',
          id: TopicType.Owned
        }
      ];

  return (
    <>
      <BasePageContainer sx={{ width: '100%', marginTop: '0px' }}>
        <Flex flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center">
            <Text variant="h2">Topics</Text>
            <Flex gap="md" alignItems="center">
              <TimePeriodDropdown allowedTimePeriods={getAtlasBeaconTimePeriods} />
              <ComparisonPeriodDropdown />
              <AppFilterButton />
              <SlButton
                ref={addTopicButtonRef}
                onClick={() => setAddTopicMenuOpen(true)}
                buttonSx={{
                  paddingLeft: '12px',
                  paddingRight: '4px'
                }}
                textSx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%'
                }}
                variant="contained"
              >
                + Add Topic
                <DownChevronWhite width="24px" height="24px" color="white" />
              </SlButton>
              <SlMenu
                anchorEl={addTopicButtonRef.current}
                open={addTopicMenuOpen}
                onClose={() => setAddTopicMenuOpen(false)}
                options={[
                  {
                    id: 'addTopic',
                    label: 'Add Topic'
                  },
                  {
                    id: 'addTopicGroup',
                    label: 'Add Topic Group'
                  }
                ]}
                selectedId={null}
                onChange={(option) => {
                  if (option.id === 'addTopicGroup') {
                    setAddTopicGroupModalOpen();
                  } else if (option.id === 'addTopic') {
                    storage.save(null, StorageSource.CREATE);
                    history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
                  }
                }}
              />
            </Flex>
          </Flex>
          <GenericNavigationTabs
            tab="summary"
            tabs={[
              {
                label: 'Summary',
                value: 'summary',
                onClick: () => {}
              }
            ]}
          />
          <Flex justifyContent="space-between" marginTop="27px" alignItems="center">
            <Flex gap="md">
              {[TopicPillFilter.All, TopicPillFilter.Topics, TopicPillFilter.TopicGroups].map((filter) => (
                <SlPill
                  key={filter}
                  selected={selectedTopicPillFilter === filter}
                  onClick={() => {
                    setSelectedTopicPillFilter(filter);
                    if (filter !== selectedTopicPillFilter) {
                      paginatorRef.current?.setPage(1);
                    }
                  }}
                >
                  {filter}
                </SlPill>
              ))}
            </Flex>
            <SlDropdownMenu options={filterOptions} selectedId={selectedTopicType} onChange={handleTopicTypeChange} />
          </Flex>
          <Flex>
            <SearchTextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={
                selectedTopicPillFilter === TopicPillFilter.TopicGroups ? 'Search topic groups' : 'Search topics'
              }
              sx={{
                marginTop: '32px'
              }}
            />
          </Flex>
          <TopicsResults
            debouncedInput={debouncedInput}
            selectedTopicType={selectedTopicType}
            selectedTopicPillFilter={selectedTopicPillFilter}
          />
        </Flex>
      </BasePageContainer>
      <DeleteTopicModal />
      <DeleteTopicGroupModal />
    </>
  );
}
